import type { BackupCipher, BackupRecoveryPackage } from "../../../../../supabase/functions/_shared/backupTypes";
import { canonical } from "../driveSync/cryptoV2";
import { fromBase64, toBase64 } from "./binary";

export const MAX_AUTOMATIC_BACKUP_BYTES = 100 * 1024 * 1024;
export const MIN_BACKUP_RECOVERY_PASSWORD_LENGTH = 8;
export type BackupKeys = {
  key: CryptoKey;
  material: string;
  recoveryPrivate: JsonWebKey;
  recoveryPublic: JsonWebKey;
  keyId: string;
};
const bytes = (value: unknown) => new TextEncoder().encode(canonical(value));
async function importBackupKey(material: string) {
  const raw = fromBase64(material);
  if (raw.length !== 32) throw new Error("sync-backup-key-invalid");
  try {
    return await crypto.subtle.importKey("raw", raw as BufferSource, "AES-GCM", false, ["encrypt", "decrypt"]);
  } finally {
    raw.fill(0);
  }
}
export async function createBackupKeys(): Promise<BackupKeys> {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const material = toBase64(raw);
  raw.fill(0);
  const recovery = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]);
  return {
    material,
    key: await importBackupKey(material),
    keyId: crypto.randomUUID(),
    recoveryPrivate: await crypto.subtle.exportKey("jwk", recovery.privateKey),
    recoveryPublic: await crypto.subtle.exportKey("jwk", recovery.publicKey),
  };
}
async function passwordKey(password: string, salt: Uint8Array) {
  const raw = new TextEncoder().encode(password);
  try {
    const source = await crypto.subtle.importKey("raw", raw, "PBKDF2", false, ["deriveKey"]);
    return await crypto.subtle.deriveKey(
      { name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations: 600000 },
      source,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  } finally {
    raw.fill(0);
  }
}
export async function sealBackupRecovery(
  keys: BackupKeys,
  password: string,
  accountId: string,
  workspaceId: string,
): Promise<BackupRecoveryPackage> {
  if (password.length < MIN_BACKUP_RECOVERY_PASSWORD_LENGTH || password.length > 1024)
    throw new Error("sync-backup-password-length");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header = {
    format: "classroom-backup-recovery" as const,
    version: 1 as const,
    accountId,
    workspaceId,
    keyId: keys.keyId,
    salt: toBase64(salt),
    iterations: 600000 as const,
    iv: toBase64(iv),
  };
  const plain = bytes({
    material: keys.material,
    recoveryPrivate: keys.recoveryPrivate,
    recoveryPublic: keys.recoveryPublic,
  });
  try {
    const cipher = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: bytes(header) },
      await passwordKey(password, salt),
      plain,
    );
    return { ...header, ciphertext: toBase64(new Uint8Array(cipher)) };
  } finally {
    plain.fill(0);
  }
}
export async function openBackupRecovery(
  pack: BackupRecoveryPackage,
  password: string,
  accountId: string,
  workspaceId: string,
): Promise<BackupKeys> {
  if (
    pack?.format !== "classroom-backup-recovery" ||
    pack.version !== 1 ||
    pack.accountId !== accountId ||
    pack.workspaceId !== workspaceId ||
    pack.iterations !== 600000 ||
    typeof pack.keyId !== "string" ||
    pack.keyId.length > 128 ||
    typeof pack.ciphertext !== "string" ||
    pack.ciphertext.length > 16384 ||
    password.length > 1024
  )
    throw new Error("sync-backup-recovery-invalid");
  const { ciphertext, ...header } = pack;
  try {
    const salt = fromBase64(pack.salt),
      iv = fromBase64(pack.iv);
    if (salt.length !== 16 || iv.length !== 12) throw new Error("format");
    const plain = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv as BufferSource, additionalData: bytes(header) },
        await passwordKey(password, salt),
        fromBase64(ciphertext) as BufferSource,
      ),
    );
    try {
      const value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(plain)) as Omit<
        BackupKeys,
        "key" | "keyId"
      >;
      await crypto.subtle.importKey("jwk", value.recoveryPrivate, { name: "ECDSA", namedCurve: "P-256" }, false, [
        "sign",
      ]);
      return { ...value, key: await importBackupKey(value.material), keyId: pack.keyId };
    } finally {
      plain.fill(0);
    }
  } catch {
    throw new Error("sync-backup-recovery-invalid");
  }
}
export async function sealAutomaticBackup(
  key: CryptoKey,
  workspaceId: string,
  objectId: string,
  value: unknown,
): Promise<BackupCipher> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header = {
    format: "classroom-auto-backup" as const,
    version: 1 as const,
    workspaceId,
    objectId,
    iv: toBase64(iv),
  };
  const plain = bytes(value);
  if (plain.length > MAX_AUTOMATIC_BACKUP_BYTES * 0.74) throw new Error("sync-backup-limit");
  try {
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: bytes(header) }, key, plain);
    return { ...header, ciphertext: toBase64(new Uint8Array(encrypted)) };
  } finally {
    plain.fill(0);
  }
}
export async function openAutomaticBackup(
  key: CryptoKey,
  workspaceId: string,
  objectId: string,
  cipher: BackupCipher,
): Promise<unknown> {
  if (
    cipher?.format !== "classroom-auto-backup" ||
    cipher.version !== 1 ||
    cipher.workspaceId !== workspaceId ||
    cipher.objectId !== objectId ||
    typeof cipher.ciphertext !== "string" ||
    cipher.ciphertext.length > MAX_AUTOMATIC_BACKUP_BYTES
  )
    throw new Error("sync-backup-format");
  try {
    const { ciphertext, ...header } = cipher;
    const iv = fromBase64(cipher.iv);
    if (iv.length !== 12) throw new Error("format");
    const plain = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv as BufferSource, additionalData: bytes(header) },
        key,
        fromBase64(ciphertext) as BufferSource,
      ),
    );
    try {
      return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(plain));
    } finally {
      plain.fill(0);
    }
  } catch {
    throw new Error("sync-backup-format");
  }
}
